// EpoxyCoating.js
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import { sliceModelXZ } from "../utils/sliceModel";
import { generateSVG, downloadSVG } from "../utils/exportUtils";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { epoxyCoatingTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import "../styles/common.css";

const LASER_CUTTER_PRESETS = {
  'Glowforge Basic': { width: 279, height: 495 },
  'Epilog Zing 16': { width: 406, height: 305 },
  'Trotec Speedy 400': { width: 1016, height: 610 },
  'Custom': { width: 10, height: 10 }
};

const EpoxyCoating = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);

  const { createVisualization, removeVisualHelpers } = useVisualHelpers();
  const pageId = useMemo(() => `epoxy-coating`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  const [isLoading, setIsLoading] = useState(true);
  const [materialThickness, setMaterialThickness] = useState(3);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [svgReady, setSvgReady] = useState(false);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [resinMode, setResinMode] = useState(false);
  const [error, setError] = useState(null);
  const [constraintWarnings, setConstraintWarnings] = useState([]);
  const [laserCutterSettings, setLaserCutterSettings] = useState({
    selected: "Glowforge Basic",
    expanded: false,
  });
  // Epoxy coating is very manual & slow → not great for rapid iteration
  const slowFabrication = useMemo(() => true, []);

  const shapesToExport = useRef([]);
  const layerLines = useRef([]);
  const extrudedMeshesRef = useRef([]);

  // 材质定义 - 木材和环氧树脂
  const woodMaterial = useMemo(
    () =>
      new THREE.MeshPhongMaterial({
        color: 0xb98f71,
        side: THREE.DoubleSide,
        shininess: 30,
      }),
    []
  );

  const resinMaterial = useMemo(
    () =>
      new THREE.MeshPhongMaterial({
        color: 0x87cefa,
        shininess: 100,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
      }),
    []
  );

  // 当前材质引用
  const currentMaterialRef = useRef(resinMode ? resinMaterial : woodMaterial);

  useEffect(() => {
    currentMaterialRef.current = resinMode ? resinMaterial : woodMaterial;
  }, [resinMode, woodMaterial, resinMaterial]);

  // 切换材质显示
  const applyMaterialToLayers = useCallback((mat) => {
    extrudedMeshesRef.current.forEach((m) => {
      m.material = mat;
      m.material.needsUpdate = true;
    });
  }, []);

  // 激光切割机尺寸检查
  // 用整体占地预检替换原来的逐层检查
const checkLaserCutterConstraints = useCallback(
  (bbox) => {
    const constraints = window.labConstraints;
    const selected =
      constraints?.machineSettings?.laserCutter?.selected ||
      laserCutterSettings.selected;
    const cutter =
      LASER_CUTTER_PRESETS[selected] ||
      LASER_CUTTER_PRESETS["Trotec Speedy 400"]; // 修改这里

      const size = new THREE.Vector3();
      bbox.getSize(size);
      const modelW = size.x; // X
      const modelH = size.z; // Z 平面当作激光机的 Y

      const fits0 = modelW <= cutter.width && modelH <= cutter.height;
      const fits90 = modelH <= cutter.width && modelW <= cutter.height;

      if (fits0 || fits90) {
        setConstraintWarnings([]);
        return { ok: true };
      }

      setConstraintWarnings([
        {
          type: "model_size_exceeded",
          layer: null,
          currentSize: { width: modelW.toFixed(1), height: modelH.toFixed(1) },
          maxSize: cutter,
          cutter: selected,
        },
      ]);

      return { ok: false };
    },
    [laserCutterSettings.selected]
  );

  // 使用 StackedLaserCutLayers2 的算法
  const createShapeFromHorizontalComponent = (uniqueVertices, compSegs) => {
    const indexSet = new Set();
    for (const seg of compSegs) {
      indexSet.add(seg.i1);
      indexSet.add(seg.i2);
    }
    const indices = [...indexSet];
    if (indices.length < 3) return null;
    const points = indices.map(
      (i) => new THREE.Vector2(uniqueVertices[i].x, uniqueVertices[i].z)
    );
    const centroid = points
      .reduce((acc, p) => acc.add(p), new THREE.Vector2())
      .divideScalar(points.length);
    points.sort(
      (a, b) =>
        Math.atan2(a.y - centroid.y, a.x - centroid.x) -
        Math.atan2(b.y - centroid.y, b.x - centroid.x)
    );
    const shape = new THREE.Shape();
    shape.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++)
      shape.lineTo(points[i].x, points[i].y);
    shape.lineTo(points[0].x, points[0].y);
    return shape;
  };

  const bakeHorizontalShapeToWorld = (shape, matrixWorld) => {
    const points = shape.getPoints();
    const bakedPoints = points.map((pt) => {
      const vec = new THREE.Vector3(pt.x, 0, pt.y).applyMatrix4(matrixWorld);
      return new THREE.Vector2(vec.x, vec.z);
    });
    return new THREE.Shape(bakedPoints);
  };

  const addHorizontalShape = (shape, extrudeSettings, pos, rot, scl) => {
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const mesh = new THREE.Mesh(geometry, currentMaterialRef.current);
    mesh.setRotationFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
    mesh.position.copy(pos);
    if (scl) mesh.scale.copy(scl);
    mesh.updateMatrixWorld();
    return mesh;
  };

  const generateHorizontalStackedLayers = useCallback(
    (obj, scene) => {
      // 清理之前的层
      extrudedMeshesRef.current.forEach((mesh) => scene.remove(mesh));
      extrudedMeshesRef.current = [];
      shapesToExport.current = [];
      layerLines.current = [];

      const bbox = new THREE.Box3().setFromObject(obj);
      const size = bbox.getSize(new THREE.Vector3());
      const layerHeight = materialThickness;
      const count = Math.ceil(size.y / layerHeight);
      const extrudedMeshes = [];

      for (let i = 0; i < count; i++) {
        const y = bbox.min.y + i * layerHeight;
        const { uniqueVertices, componentSegments } = sliceModelXZ(
          obj,
          scene,
          y
        );
        if (!uniqueVertices || !componentSegments) continue;

        componentSegments.forEach((segs) => {
          if (segs.length < 3) return;
          const shape = createShapeFromHorizontalComponent(
            uniqueVertices,
            segs
          );
          if (!shape) return;

          const extruded = addHorizontalShape(
            shape,
            {
              depth: -3,
              bevelEnabled: true,
              steps: 2,
              bevelSize: 0,
              bevelThickness: 0,
              bevelSegments: 2,
            },
            new THREE.Vector3(obj.position.x, y, obj.position.z),
            obj.rotation,
            obj.scale
          );

          scene.add(extruded);
          extrudedMeshesRef.current.push(extruded);

          const line = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(shape.getPoints()),
            new THREE.LineBasicMaterial({ color: 0x000000 })
          );
          //extruded.add(line);
          layerLines.current.push(line);
          shapesToExport.current.push(
            bakeHorizontalShapeToWorld(shape, extruded.matrixWorld)
          );
          extrudedMeshes.push(extruded);
        });
      }

      // 更新模型尺寸信息
      setModelDimensions({
        width: size.x.toFixed(2),
        height: size.y.toFixed(2),
        depth: size.z.toFixed(2),
        layers: count,
      });
      setSvgReady(true);

      return {
        shapesToExport: shapesToExport.current,
        extrudedMeshes,
        layerLines: layerLines.current,
      };
    },
    [materialThickness]
  );

  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);

    // 清旧
    if (objRef.current) {
      sceneRef.current?.remove(objRef.current);
      objRef.current.geometry?.dispose();
      if (Array.isArray(objRef.current.material))
        objRef.current.material.forEach((m) => m.dispose());
      else objRef.current.material?.dispose();
      objRef.current = null;
    }
    // 清辅助 & 旧层
    removeVisualHelpers(sceneRef.current);
    extrudedMeshesRef.current.forEach((m) => sceneRef.current?.remove(m));
    extrudedMeshesRef.current = [];
    shapesToExport.current = [];
    layerLines.current = [];

    const loader = new STLLoader();
    const path = stlFile || "/models/twistT.stl";

    loader.load(
      path,
      (geometry) => {
        try {
          geometry.computeBoundingBox();
          const center = new THREE.Vector3();
          geometry.boundingBox.getCenter(center);
          geometry.translate(-center.x, -center.y, -center.z);
          geometry.rotateX(-Math.PI / 2);
          geometry.rotateY(-Math.PI / 2);

          const material = new THREE.MeshStandardMaterial({
            color: 0xd2a679,
            metalness: 0.1,
            roughness: 0.6,
            transparent: true,
            opacity: 0.2,
          });

          const mesh = new THREE.Mesh(geometry, material);
          mesh.updateMatrixWorld(true);

          // 对齐底部
          const preBox = new THREE.Box3().setFromObject(mesh);
          mesh.position.y = -preBox.min.y;
          mesh.updateMatrixWorld(true);

          // ★ 先做整体占地预检（XZ）
          const bbox = new THREE.Box3().setFromObject(mesh);
          const fit = checkLaserCutterConstraints(bbox);

          if (!fit.ok) {
            // 不通过：不 add、不生成层
            setSvgReady(false);
            setIsLoading(false);
            return;
          }

          // 通过才继续
          objRef.current = mesh;
          sceneRef.current.add(mesh);

          // 生成层
          const horiz = generateHorizontalStackedLayers(mesh, sceneRef.current);

          // 可视化
          const group = new THREE.Group();
          horiz.extrudedMeshes.forEach((l) => group.add(l.clone()));
          const box = new THREE.Box3().setFromObject(group);
          createVisualization(group, sceneRef.current, box);

          setIsLoading(false);
        } catch (e) {
          console.error(e);
          setError("Model loading failed");
          setIsLoading(false);
        }
      },
      undefined,
      (err) => {
        console.error("Failed to load model", err);
        setError("Model loading failed");
        setIsLoading(false);
      }
    );
  }, [
    stlFile,
    removeVisualHelpers,
    createVisualization,
    generateHorizontalStackedLayers,
    checkLaserCutterConstraints,
  ]);

  // 当材料厚度改变时重新生成层
  useEffect(() => {
    if (objRef.current && !isLoading && constraintWarnings.length === 0) {
      const res = generateHorizontalStackedLayers(
        objRef.current,
        sceneRef.current
      );
      setSvgReady((res?.shapesToExport?.length ?? 0) > 0);
    }
  }, [
    materialThickness,
    generateHorizontalStackedLayers,
    isLoading,
    constraintWarnings.length,
  ]);

  useEffect(() => {
    if (!objRef.current) return;
    // 用当前 mesh 的 bbox 再跑一次整体尺寸检查
    const bbox = new THREE.Box3().setFromObject(objRef.current);
    const fit = checkLaserCutterConstraints(bbox);
    // 如果合格且已有切片，才允许导出
    setSvgReady(fit.ok && (shapesToExport.current?.length ?? 0) > 0);
  }, [laserCutterSettings.selected, checkLaserCutterConstraints]);

  // 当树脂模式改变时切换材质
  useEffect(() => {
    if (!isLoading) {
      applyMaterialToLayers(resinMode ? resinMaterial : woodMaterial);
    }
  }, [
    resinMode,
    resinMaterial,
    woodMaterial,
    applyMaterialToLayers,
    isLoading,
  ]);

const exportSVG = useCallback(() => {
  if (constraintWarnings.length > 0) {
    console.warn("Cannot export: model exceeds laser cutter constraints");
    return;
  }
  
  if (!shapesToExport.current || shapesToExport.current.length === 0) {
    console.warn("No shapes to export");
    alert("没有可导出的形状数据");
    return;
  }

  console.log("Exporting ALL shapes for epoxy coating...");
  console.log("Total shapes:", shapesToExport.current.length);
  console.log("Sample shape:", shapesToExport.current[0]);

  // 创建包含所有形状的单个 SVG
  const createAllShapesSVG = (allShapes) => {
    const svgNamespace = "http://www.w3.org/2000/svg";
    const svgElement = document.createElementNS(svgNamespace, "svg");
    
    // 计算所有形状的总边界框
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    allShapes.forEach(shape => {
      if (!shape) return;
      const points = shape.getPoints();
      points.forEach(point => {
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
      });
    });
    
    // 添加边距
    const padding = 20;
    const width = maxX - minX + 2 * padding;
    const height = maxY - minY + 2 * padding;
    
    // 设置 SVG 属性
    svgElement.setAttribute("width", `${width}mm`);
    svgElement.setAttribute("height", `${height}mm`);
    svgElement.setAttribute("viewBox", `${minX - padding} ${minY - padding} ${width} ${height}`);
    
    // 添加注释
    const comment = document.createComment(`Epoxy Coating - All ${allShapes.length} shapes - Material thickness: ${materialThickness}mm`);
    svgElement.appendChild(comment);
    
    // 添加所有形状
    allShapes.forEach((shape, index) => {
      if (!shape) return;
      
      const points = shape.getPoints();
      if (points.length === 0) return;
      
      const pathElement = document.createElementNS(svgNamespace, "path");
      
      // 构建路径 - 使用形状的原始坐标
      let pathData = `M ${points[0].x},${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        pathData += ` L ${points[i].x},${points[i].y}`;
      }
      pathData += ' Z';
      
      pathElement.setAttribute("d", pathData);
      pathElement.setAttribute("stroke", "black");
      pathElement.setAttribute("stroke-width", "0.3");
      pathElement.setAttribute("fill", "none");
      pathElement.setAttribute("id", `shape_${index}`);
      
      // 如果形状有层信息，添加到 class 中
      if (shape.layerIndex !== undefined) {
        pathElement.setAttribute("class", `layer_${shape.layerIndex}`);
      }
      
      svgElement.appendChild(pathElement);
    });
    
    return svgElement;
  };

  // 按层分组创建单独的 SVG 文件
  const createLayerSVGs = (allShapes) => {
    // 按层分组
    const layerGroups = {};
    
    allShapes.forEach((shape, index) => {
      let layerIndex = 0;
      
      // 尝试获取层索引
      if (shape.layerIndex !== undefined) {
        layerIndex = shape.layerIndex;
      } else if (shape.userData && shape.userData.layerIndex !== undefined) {
        layerIndex = shape.userData.layerIndex;
      } else {
        // 如果没有层信息，估算层索引
        const totalLayers = Math.ceil((modelDimensions?.height || 50) / materialThickness);
        layerIndex = Math.floor(index / Math.max(1, Math.floor(allShapes.length / totalLayers)));
      }
      
      if (!layerGroups[layerIndex]) {
        layerGroups[layerIndex] = [];
      }
      layerGroups[layerIndex].push(shape);
    });
    
    console.log("Layer grouping:", Object.keys(layerGroups).map(k => `Layer ${k}: ${layerGroups[k].length} shapes`));
    
    const svgFiles = [];
    
    Object.keys(layerGroups).forEach(layerKey => {
      const layerIndex = parseInt(layerKey);
      const layerShapes = layerGroups[layerIndex];
      
      if (layerShapes.length === 0) return;
      
      const svgNamespace = "http://www.w3.org/2000/svg";
      const svgElement = document.createElementNS(svgNamespace, "svg");
      
      // 计算该层的边界框
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      layerShapes.forEach(shape => {
        const points = shape.getPoints();
        points.forEach(point => {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        });
      });
      
      const padding = 15;
      const width = maxX - minX + 2 * padding;
      const height = maxY - minY + 2 * padding;
      
      svgElement.setAttribute("width", `${width}mm`);
      svgElement.setAttribute("height", `${height}mm`);
      svgElement.setAttribute("viewBox", `${minX - padding} ${minY - padding} ${width} ${height}`);
      
      // 添加注释
      const comment = document.createComment(`Epoxy Coating Layer ${layerIndex + 1} - ${layerShapes.length} shapes - Thickness: ${materialThickness}mm`);
      svgElement.appendChild(comment);
      
      // 添加该层的所有形状
      layerShapes.forEach((shape, shapeIndex) => {
        const points = shape.getPoints();
        if (points.length === 0) return;
        
        const pathElement = document.createElementNS(svgNamespace, "path");
        
        let pathData = `M ${points[0].x},${points[0].y}`;
        for (let i = 1; i < points.length; i++) {
          pathData += ` L ${points[i].x},${points[i].y}`;
        }
        pathData += ' Z';
        
        pathElement.setAttribute("d", pathData);
        pathElement.setAttribute("stroke", "black");
        pathElement.setAttribute("stroke-width", "0.4");
        pathElement.setAttribute("fill", "none");
        pathElement.setAttribute("id", `shape_${shapeIndex}`);
        
        svgElement.appendChild(pathElement);
      });
      
      svgFiles.push({
        element: svgElement,
        filename: `epoxy_layer_${String(layerIndex + 1).padStart(2, '0')}.svg`,
        layerIndex: layerIndex,
        shapeCount: layerShapes.length
      });
    });
    
    return svgFiles;
  };

  // 下载 SVG 文件
  const downloadSVGFile = (svgElement, filename) => {
    const svgString = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = filename;
    downloadLink.style.display = "none";
    
    document.body.appendChild(downloadLink);
    downloadLink.click();
    
    setTimeout(() => {
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
    }, 100);
  };

  try {
    // 首先导出包含所有形状的单个文件
    const allShapesSvg = createAllShapesSVG(shapesToExport.current);
    downloadSVGFile(allShapesSvg, "epoxy_coating_all_shapes.svg");
    
    // 然后按层分别导出
    const layerSvgs = createLayerSVGs(shapesToExport.current);
    
    layerSvgs.forEach((svgFile, index) => {
      setTimeout(() => {
        downloadSVGFile(svgFile.element, svgFile.filename);
        console.log(`Exported ${svgFile.filename} with ${svgFile.shapeCount} shapes`);
      }, (index + 1) * 400);
    });
    
    const totalFiles = layerSvgs.length + 1;
    console.log(`Successfully prepared ${totalFiles} SVG files for download`);
    
    setTimeout(() => {
      alert(`成功导出 ${totalFiles} 个 SVG 文件:\n` +
            `- 1 个包含所有 ${shapesToExport.current.length} 个形状的总文件\n` +
            `- ${layerSvgs.length} 个分层文件\n` +
            `每层厚度: ${materialThickness}mm`);
    }, totalFiles * 400);
    
  } catch (error) {
    console.error("Error exporting SVG:", error);
    alert("导出失败：" + error.message);
  }
}, [modelDimensions, materialThickness, constraintWarnings.length]);

  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );
    loadModel();
    return () => {
      extrudedMeshesRef.current.forEach((layer) =>
        sceneRef.current?.remove(layer)
      );
      if (sceneRef.current) {
        removeVisualHelpers(sceneRef.current);
      }
      cleanup();
    };
  }, [loadModel]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container" />

      {/* 激光切割机尺寸警告 */}
      {constraintWarnings.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {constraintWarnings.map((warning, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "#ff9800",
                color: "white",
                padding: "12px 16px",
                borderRadius: "6px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                maxWidth: "90vw",
                width: "fit-content",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <span style={{ fontSize: "16px", marginRight: "8px" }}>⚠️</span>
                <strong>
                  {warning.type === "model_size_exceeded"
                    ? `Model Exceeds ${warning.cutter} Size`
                    : `Layer ${warning.layer} Exceeds ${warning.cutter} Size`}
                </strong>
              </div>
              <div style={{ fontSize: "13px" }}>
                <div>
                  Current: {warning.currentSize.width} ×{" "}
                  {warning.currentSize.height} mm
                </div>
                <div>
                  Laser Cutter Max: {warning.maxSize.width} ×{" "}
                  {warning.maxSize.height} mm
                </div>
              </div>
              <button
                onClick={() =>
                  setConstraintWarnings((prev) =>
                    prev.filter((_, i) => i !== index)
                  )
                }
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="panel-toggle-button"
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {!isLoading && expanded && (
        <div className="controls-panel">
          <div className="panel-title-row">
            <div className="panel-title">Epoxy Coating</div>
            {slowFabrication && (
              <span
                className="iteration-badge badge--warn"
                title="Sheet cutting + stacking + long epoxy cure & sanding cycles"
              >
                Not ideal for quick iteration
              </span>
            )}
          </div>

          {/* 保留原始的 Epoxy Coating 描述 */}
          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                This tool simulates a fabrication process where a model is
                approximated using stacked laser-cut sheets, which are then
                coated with epoxy resin to create a smooth, solid surface.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Useful for producing lightweight but visually solid forms where
                full 3D printing is impractical or too costly.
              </p>
            </div>
          </div>

<div className="panel-section">
  <div className="section-header"><h4>Time & Effort</h4></div>
  <div className="section-content">
    <ul style={{ marginLeft: 16, lineHeight: 1.5, fontSize: 13 }}>
      <li><strong>Sheet prep</strong>: laser-cutting, labeling, and dry-fit stacking to verify alignment.</li>
      <li><strong>Bonding & clamping</strong>: gluing layers (or pins/rods), step-by-step press with cure time between stacks.</li>
      <li><strong>Rough shaping</strong>: rasp/sanding to knock down the stair-steps before resin.</li>
      <li><strong>Epoxy mixing & seal coat</strong>: thin coat to seal pores; bubble management (heat gun), overnight cure.</li>
      <li><strong>Build coats</strong>: 1–3 additional coats depending on smoothness; each coat requires curing.</li>
      <li><strong>Leveling & finish</strong>: progressive wet-sanding (e.g., 320→800→1500), optional polish or clear top-coat.</li>
      <li><strong>Safety</strong>: respirator, gloves, good ventilation; comply with resin MSDS and disposal rules.</li>
    </ul>
    <p style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
      Recommendation: validate scale and silhouette with fewer, thicker layers first; only then commit to full stacking and resin cycles.
    </p>
  </div>
</div>


          {modelDimensions && (
            <div className="info-box">
              <p>
                <strong>Total Layers:</strong> {modelDimensions.layers}
              </p>
            </div>
          )}

          <div className="buttons-container">
            <button
              onClick={exportSVG}
              className="action-button"
              disabled={!svgReady || constraintWarnings.length > 0}
            >
              EXPORT SVG
            </button>

            <button
              onClick={() => setTutorialVisible((prev) => !prev)}
              className="action-button"
            >
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="loading-box">Loading model...</div>}
      {error && <div className="error-box">Error: {error}</div>}

      {/* 保留原始的 Epoxy Coating 教程 */}
      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible(false)}
        tutorialContent={epoxyCoatingTutorial}
      />
    </div>
  );
};

export default EpoxyCoating;
